import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { setupInterceptors } from './interceptors/'
import { HTTP_URLS } from '@/config/http'

export interface ApiError {
  message: string
  status: number
  data?: any
}

// URL base do Sanctum para obter cookie CSRF
const SANCTUM_BASE_URL = HTTP_URLS.sanctum

// Constantes
const DEFAULT_TIMEOUT = 10000
const DEFAULT_HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json'
}

/**
 * Classe base para clientes HTTP
 * Elimina duplicação de código entre ApiClient e WebClient (princípio DRY)
 */
abstract class BaseHttpClient {
  protected client: AxiosInstance
  protected clientName: string

  constructor(baseURL: string, clientName: string) {
    this.clientName = clientName
    this.client = axios.create({
      baseURL,
      timeout: DEFAULT_TIMEOUT,
      headers: DEFAULT_HEADERS,
      withCredentials: true // Necessário para cookies do Sanctum (CSRF)
    })

    // Usar interceptors centralizados na instância específica
    // setupInterceptors aceita AxiosInstance como parâmetro
    setupInterceptors(this.client as any)
  }

  protected handleError(error: any): ApiError {
    if (error.response) {
      return {
        message: error.response.data?.message || 'Erro na requisição',
        status: error.response.status,
        data: error.response.data
      }
    }
    return { message: 'Erro de conexão', status: 0 }
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config)
    return response.data
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config)
    return response.data
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config)
    return response.data
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config)
    return response.data
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config)
    return response.data
  }

  async upload<T>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, formData, {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers
      }
    })
    return response.data
  }

  async download(url: string, config?: AxiosRequestConfig): Promise<Blob> {
    const response = await this.client.get(url, {
      ...config,
      responseType: 'blob'
    })
    return response.data
  }
}

/**
 * Cliente para rotas API (com prefixo /api)
 */
class ApiClient extends BaseHttpClient {
  constructor() {
    super(HTTP_URLS.api, 'ApiClient')
  }

  /**
   * Obtém o cookie CSRF do Sanctum antes de fazer requisições que exigem CSRF
   * Necessário quando o Sanctum está em modo stateful
   */
  async getCsrfCookie(): Promise<boolean> {
    try {
      await axios.get(`${SANCTUM_BASE_URL}/sanctum/csrf-cookie`, {
        withCredentials: true
      })
      return true
    } catch (error) {
      console.warn('Erro ao obter cookie CSRF:', error)
      return false
    }
  }
}

/**
 * Cliente para rotas web (sem prefixo /api)
 * Sempre usa URL absoluta do backend para garantir que funcione tanto via Vite dev server quanto Docker
 */
class WebClient extends BaseHttpClient {
  constructor() {
    super(HTTP_URLS.backend, 'WebClient')
  }
}

export const apiClient = new ApiClient()
export const webClient = new WebClient()

/**
 * Função helper para obter cookie CSRF do Sanctum
 * Exportada separadamente para garantir acessibilidade
 */
export async function getCsrfCookie(): Promise<boolean> {
  try {
    await axios.get(`${SANCTUM_BASE_URL}/sanctum/csrf-cookie`, {
      withCredentials: true
    })
    return true
  } catch (error) {
    console.warn('Erro ao obter cookie CSRF:', error)
    return false
  }
}
